/**
 * Zeroday Scalper. The lines are computed on 1-minute bars and then stepped
 * onto the chart's grid. Stars are NQ signals drawn by the primitive, and
 * only on the 1-minute SPX, SPY, and QQQ charts.
 */

import 'openalgo-charts/indicators';
import {
  registerIndicator,
  type Bar,
  type IPrimitive,
  type IndicatorAttachContext,
  type IndicatorSettings,
  type IndicatorStore,
  type IndicatorValues,
  type PrimitiveHost,
  type PrimitiveRenderContext,
} from 'openalgo-charts';
import { chartBarsService } from '../../services/chartBarsService';
import { minuteKey, scalperStars, type ScalperBar, type ScalperStar } from '../nq-scalper/signals';
import { dailyVwap, twoDayVwap, weeklyVwap, type VwapPoint } from './avwap';
import { hasVolume, type CandleBar, type ChartInterval } from './barSource';
import { emaOnBuckets, projectStep, type EmaPoint } from './ema';
import { bucket15, bucket65 } from './shareMinutes';
import { markerPathRadius, placeStars, starPath, starRadius, starsOnSymbol, starY } from './stars';

export const ZERODAY_ID = 'zeroday-scalper';

const BUY_COLOR = '#3DDC6A';
const SELL_COLOR = '#D8483B';
const STAR_INNER = 2.7 / 6.5;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isCandleBar(value: unknown): value is CandleBar {
  if (!isRecord(value)) return false;
  return typeof value.time === 'number'
    && typeof value.open === 'number'
    && typeof value.high === 'number'
    && typeof value.low === 'number'
    && typeof value.close === 'number'
    && typeof value.volume === 'number';
}

function isStar(value: unknown): value is ScalperStar {
  if (!isRecord(value)) return false;
  return typeof value.minute === 'number'
    && (value.side === 'long' || value.side === 'short')
    && (value.tier === 10 || value.tier === 15)
    && typeof value.provisional === 'boolean';
}

function readMinutes(store: IndicatorStore): CandleBar[] {
  const value = store.minutes;
  if (!Array.isArray(value)) return [];
  return value.filter(isCandleBar).slice().sort((a, b) => a.time - b.time);
}

function readStars(store: IndicatorStore): ScalperStar[] {
  const value = store.stars;
  if (!Array.isArray(value)) return [];
  return value.filter(isStar);
}

function readNqMinutes(store: IndicatorStore): CandleBar[] {
  const value = store.nqMinutes;
  if (!Array.isArray(value)) return [];
  return value.filter(isCandleBar).slice().sort((a, b) => a.time - b.time);
}

function readForming(store: IndicatorStore): ScalperBar | null {
  const value = store.forming;
  if (!isRecord(value)) return null;
  if (typeof value.minute !== 'number' || typeof value.close !== 'number') return null;
  return { minute: value.minute, close: value.close };
}

function flag(settings: Readonly<IndicatorSettings>, key: string): boolean {
  const value = settings[key];
  return value === undefined ? true : value === true;
}

function numberSetting(settings: Readonly<IndicatorSettings>, key: string, fallback: number): number {
  const value = settings[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function stringSetting(settings: Readonly<IndicatorSettings>, key: string, fallback: string): string {
  const value = settings[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export function chartIntervalFromCode(code: string | undefined): ChartInterval {
  const text = (code ?? '').trim().toLowerCase();
  if (text === '1mo' || text === '1mth') return { unit: 'month', count: 1 };
  const match = /^(\d+)(m|h|d|w)$/.exec(text);
  if (!match) return { unit: 'minute', count: 1 };
  const count = Number(match[1]);
  if (match[2] === 'h') return { unit: 'hour', count };
  if (match[2] === 'd') return { unit: 'day', count };
  if (match[2] === 'w') return { unit: 'week', count };
  return { unit: 'minute', count: Math.max(1, count) };
}

/** End of each chart bar, in UTC milliseconds. The open bar reads the newest minute. */
function sampleTimes(bars: readonly Bar[]): number[] {
  const times: number[] = [];
  for (let index = 0; index < bars.length; index += 1) {
    const next = bars[index + 1];
    times.push(next ? next.time * 1000 - 1 : Number.POSITIVE_INFINITY);
  }
  return times;
}

function column(
  enabled: boolean,
  points: readonly EmaPoint[] | readonly VwapPoint[],
  times: readonly number[],
): (number | null)[] {
  if (!enabled) return times.map(() => null);
  return projectStep(points, times);
}

function projectLines(
  bars: readonly Bar[],
  settings: Readonly<IndicatorSettings>,
  store: IndicatorStore,
): IndicatorValues {
  const times = sampleTimes(bars);
  const minutes = readMinutes(store);
  const volumeLines = hasVolume(minutes);
  const ema15 = emaOnBuckets(minutes, bucket15, 8);
  const ema65 = emaOnBuckets(minutes, bucket65, 8);
  return {
    daily: column(volumeLines && flag(settings, 'showDaily'), dailyVwap(minutes), times),
    weekly: column(volumeLines && flag(settings, 'showWeekly'), weeklyVwap(minutes), times),
    twoDay: column(volumeLines && flag(settings, 'showTwoDay'), twoDayVwap(minutes), times),
    ema15: column(flag(settings, 'showEma15'), ema15, times),
    ema65: column(flag(settings, 'showEma65'), ema65, times),
  };
}

function starsFrom(minutes: readonly CandleBar[], forming: ScalperBar | null): ScalperStar[] {
  return scalperStars(
    minutes.map((bar) => ({ minute: bar.time, close: bar.close })),
    forming,
  );
}

function writeSeries(
  store: IndicatorStore,
  minutes: CandleBar[],
  nqMinutes: CandleBar[],
  forming: ScalperBar | null,
): void {
  store.minutes = minutes;
  store.nqMinutes = nqMinutes;
  store.forming = forming;
  store.stars = starsFrom(nqMinutes, forming);
}

function upsertLiveMinute(minutes: readonly CandleBar[], bar: CandleBar): CandleBar[] {
  const next = minutes.slice();
  const index = next.findIndex((item) => item.time === bar.time);
  if (index < 0) {
    next.push(bar);
    next.sort((a, b) => a.time - b.time);
    return next;
  }
  const prev = next[index];
  next[index] = {
    time: bar.time,
    open: prev.open,
    high: Math.max(prev.high, bar.high),
    low: Math.min(prev.low, bar.low),
    close: bar.close,
    volume: bar.volume > 0 ? bar.volume : prev.volume,
  };
  return next;
}

function replaceMinutes(minutes: readonly CandleBar[], bars: readonly CandleBar[]): CandleBar[] {
  const byTime = new Map(minutes.map((bar) => [bar.time, bar]));
  for (const bar of bars) byTime.set(bar.time, bar);
  return [...byTime.values()].sort((a, b) => a.time - b.time);
}

function latestMinute(minutes: readonly { time: number }[]): number {
  return minutes.reduce((max, bar) => Math.max(max, bar.time), Number.NEGATIVE_INFINITY);
}

/**
 * The forming quote drives the star only while its minute is still the latest.
 * A quote for an older minute is a late read of a bar that has already closed.
 */
export function liveNqForming(
  minutes: readonly { time: number }[],
  forming: ScalperBar | null,
): ScalperBar | null {
  if (!forming) return null;
  const latest = latestMinute(minutes);
  if (Number.isFinite(latest) && forming.minute < latest) return null;
  return forming;
}

function heldClose(forming: ScalperBar): CandleBar {
  return {
    time: forming.minute,
    open: forming.close,
    high: forming.close,
    low: forming.close,
    close: forming.close,
    volume: 0,
  };
}

/**
 * NQ minutes for the star pass.
 *
 * The polled row for the minute that is still open is not an official close.
 * Drop it so the one-second forming quote can print the star. When that minute
 * ends, keep the last quote until the next poll replaces it.
 */
export function nqMinutesForStars(
  minutes: readonly CandleBar[],
  forming: ScalperBar | null,
  previous: ScalperBar | null,
): CandleBar[] {
  const next = minutes.slice();
  const live = liveNqForming(minutes, forming);
  if (
    previous
    && live
    && previous.minute < live.minute
    && !next.some((bar) => bar.time === previous.minute)
  ) {
    next.push(heldClose(previous));
  }
  const kept = live ? next.filter((bar) => bar.time !== live.minute) : next;
  kept.sort((a, b) => a.time - b.time);
  return kept;
}

interface BoundStudy {
  symbol: string;
  ctx: IndicatorAttachContext;
  primitive: StarPrimitive;
}

const boundStudies = new Set<BoundStudy>();
let nqPollDown = false;
const nqListeners = new Set<(down: boolean) => void>();

function nqIsDown(): boolean {
  if (nqPollDown) return true;
  for (const study of boundStudies) {
    if (study.ctx.store.nqError !== undefined) return true;
  }
  return false;
}

function emitNqStatus(): void {
  const down = nqIsDown();
  for (const listener of nqListeners) listener(down);
}

export function subscribeZerodayNqStatus(listener: (down: boolean) => void): () => void {
  nqListeners.add(listener);
  listener(nqIsDown());
  return (): void => {
    nqListeners.delete(listener);
  };
}

export function setZerodayNqPollDown(down: boolean): void {
  nqPollDown = down;
  emitNqStatus();
}

function paintStudy(study: BoundStudy): void {
  study.ctx.requestRecompute();
  study.primitive.requestUpdate();
}

/** One live minute. Volume of 0 keeps the stored share count, so AVWAP stays put. */
export function pushZerodayMinute(symbol: string, bar: CandleBar): void {
  const root = symbol.trim().toUpperCase().replace(/\.CME$/, '');
  const minute: CandleBar = { ...bar, time: minuteKey(bar.time) };
  if (!Number.isFinite(minute.time)) return;
  for (const study of boundStudies) {
    if (study.symbol !== root) continue;
    const minutes = upsertLiveMinute(readMinutes(study.ctx.store), minute);
    if (root === 'NQ') {
      writeSeries(study.ctx.store, minutes, minutes, readForming(study.ctx.store));
    } else {
      study.ctx.store.minutes = minutes;
    }
    paintStudy(study);
  }
}

/** Official NQ minutes from the 30-second poll. They replace matching timestamps. */
export function pushZerodayNqMinutes(bars: readonly CandleBar[]): void {
  const stamped = bars.map((bar) => ({ ...bar, time: minuteKey(bar.time) }));
  for (const study of boundStudies) {
    const forming = liveNqForming(stamped, readForming(study.ctx.store));
    const merged = replaceMinutes(readNqMinutes(study.ctx.store), stamped);
    const nq = forming ? merged.filter((bar) => bar.time !== forming.minute) : merged;
    const minutes = study.symbol === 'NQ'
      ? replaceMinutes(readMinutes(study.ctx.store), stamped)
      : readMinutes(study.ctx.store);
    writeSeries(study.ctx.store, minutes, nq, forming);
    paintStudy(study);
  }
}

function sameForming(left: ScalperBar | null, right: ScalperBar | null): boolean {
  if (left === null || right === null) return left === right;
  return left.minute === right.minute && left.close === right.close;
}

export function setZerodayForming(forming: ScalperBar | null): void {
  for (const study of boundStudies) {
    const previous = readForming(study.ctx.store);
    if (sameForming(previous, forming)) continue;
    const stored = readNqMinutes(study.ctx.store);
    const live = liveNqForming(stored, forming);
    writeSeries(
      study.ctx.store,
      readMinutes(study.ctx.store),
      nqMinutesForStars(stored, forming, previous),
      live,
    );
    // The lines do not read the forming minute. Recomputing them here
    // redrew every VWAP on the one-second poll. The star pass is this
    // minute's close against the last ten, and the primitive repaints it.
    study.primitive.requestUpdate();
  }
}

function chartSymbol(ctx: IndicatorAttachContext): string {
  const named = ctx.symbol?.() ?? ctx.dataContext?.()?.symbol ?? '';
  return named.trim().toUpperCase().replace(/\.CME$/, '');
}

function paintMarker(
  canvas: CanvasRenderingContext2D,
  marker: string,
  x: number,
  y: number,
  radius: number,
  color: string,
  filled: boolean,
  dpr: number,
): void {
  const weight = Math.max(1, dpr);
  const ink = markerPathRadius(radius, filled, weight);
  canvas.fillStyle = color;
  canvas.strokeStyle = color;
  canvas.lineWidth = weight;
  canvas.lineJoin = 'round';
  canvas.lineCap = 'round';
  if (marker === 'circle') {
    canvas.beginPath();
    canvas.arc(x, y, ink, 0, Math.PI * 2);
  } else if (marker === 'square') {
    canvas.beginPath();
    canvas.rect(x - ink, y - ink, ink * 2, ink * 2);
  } else if (marker === 'diamond') {
    canvas.beginPath();
    canvas.moveTo(x, y - ink);
    canvas.lineTo(x + ink, y);
    canvas.lineTo(x, y + ink);
    canvas.lineTo(x - ink, y);
    canvas.closePath();
  } else if (marker === 'triangle') {
    canvas.beginPath();
    canvas.moveTo(x, y - ink);
    canvas.lineTo(x + ink, y + ink);
    canvas.lineTo(x - ink, y + ink);
    canvas.closePath();
  } else {
    const outer = new Path2D(starPath(x, y, radius, radius * STAR_INNER));
    if (filled) {
      canvas.fill(outer);
      return;
    }
    // A stroke is centered on the path, and the star's points stick out past
    // the fill. A hole cut from the same outer path keeps one outer edge.
    const holeRadius = Math.max(weight / 2, radius - weight);
    const hole = new Path2D(starPath(x, y, holeRadius, holeRadius * STAR_INNER));
    const ring = new Path2D();
    ring.addPath(outer);
    ring.addPath(hole);
    canvas.fill(ring, 'evenodd');
    return;
  }
  if (filled) canvas.fill();
  else canvas.stroke();
}

class StarPrimitive implements IPrimitive {
  private host: PrimitiveHost | null = null;

  private readonly ctx: IndicatorAttachContext;

  constructor(ctx: IndicatorAttachContext) {
    this.ctx = ctx;
  }

  zOrder(): 'top' {
    return 'top';
  }

  attached(host: PrimitiveHost): void {
    this.host = host;
  }

  detached(): void {
    this.host = null;
  }

  requestUpdate(): void {
    this.host?.requestUpdate();
  }

  draw(canvas: CanvasRenderingContext2D, rc: PrimitiveRenderContext): void {
    const settings = this.ctx.settings();
    if (!flag(settings, 'showStars')) return;
    const bars = rc.bars?.();
    if (!bars || bars.length === 0) return;
    const stars = readStars(this.ctx.store);
    if (stars.length === 0) return;
    if (!starsOnSymbol(chartSymbol(this.ctx))) return;
    const interval = chartIntervalFromCode(this.ctx.interval?.() ?? this.ctx.dataContext?.()?.interval);
    const offset = numberSetting(settings, 'offset', 16);
    const size = numberSetting(settings, 'size', 8);
    const marker = stringSetting(settings, 'marker', 'star');
    const buy = stringSetting(settings, 'buyColor', BUY_COLOR);
    const sell = stringSetting(settings, 'sellColor', SELL_COLOR);
    const placed = placeStars(
      stars,
      bars.map((bar) => ({ time: bar.time * 1000, high: bar.high, low: bar.low })),
      interval,
      offset,
    );
    const dpr = rc.dpr || 1;
    canvas.save();
    for (const star of placed) {
      const candle = bars[star.index];
      if (!candle) continue;
      const edge = star.side === 'short' ? candle.high : candle.low;
      const y = starY(rc.priceScale.priceToY(edge), star.side, offset * (star.slot + 1)) * dpr;
      const x = rc.timeScale.indexToX(star.index) * dpr;
      const radius = starRadius(star.tier, size) * dpr;
      paintMarker(
        canvas,
        marker,
        x,
        y,
        radius,
        star.side === 'long' ? buy : sell,
        star.tier === 15,
        dpr,
      );
    }
    canvas.restore();
  }
}

function attachZeroday(ctx: IndicatorAttachContext): () => void {
  const primitive = new StarPrimitive(ctx);
  ctx.addPrimitive?.(primitive);
  const study: BoundStudy = { symbol: '', ctx, primitive };
  boundStudies.add(study);
  let dead = false;
  let generation = 0;
  let applied = '';

  const sync = (): void => {
    if (dead || ctx.signal?.aborted) return;
    const symbol = chartSymbol(ctx);
    study.symbol = symbol;
    if (!symbol) {
      if (applied === '') return;
      applied = '';
      generation += 1;
      writeSeries(ctx.store, [], [], null);
      ctx.setDataStatus?.({ state: 'empty' });
      ctx.requestRecompute();
      primitive.requestUpdate();
      return;
    }
    if (symbol === applied) return;
    applied = symbol;
    const gen = generation + 1;
    generation = gen;
    ctx.setDataStatus?.({ state: 'loading' });
    void (async (): Promise<void> => {
      try {
        const minutes = await chartBarsService.getIndicatorMinutes(symbol);
        if (dead || gen !== generation) return;
        let nqMinutes = minutes;
        if (symbol !== 'NQ') {
          try {
            nqMinutes = await chartBarsService.getIndicatorMinutes('NQ');
            delete ctx.store.nqError;
          } catch (error: unknown) {
            if (dead || gen !== generation) return;
            nqMinutes = [];
            ctx.store.nqError = error;
          }
        } else {
          delete ctx.store.nqError;
        }
        if (dead || gen !== generation) return;
        writeSeries(ctx.store, minutes, nqMinutes, readForming(ctx.store));
        ctx.setDataStatus?.({ state: minutes.length > 0 ? 'ready' : 'empty' });
      } catch (error: unknown) {
        if (dead || gen !== generation) return;
        writeSeries(ctx.store, [], [], null);
        ctx.setDataStatus?.({ state: 'error', error });
      }
      if (dead || gen !== generation) return;
      emitNqStatus();
      ctx.requestRecompute();
      primitive.requestUpdate();
    })();
  };

  ctx.setDataRetry?.(() => {
    applied = '';
    sync();
  });
  const unsubscribe = ctx.subscribeDataChanges?.(() => {
    const symbol = chartSymbol(ctx);
    if (symbol !== applied) sync();
  }) ?? ((): void => {});
  const onAbort = (): void => {
    dead = true;
  };
  ctx.signal?.addEventListener('abort', onAbort, { once: true });
  sync();

  return (): void => {
    dead = true;
    generation += 1;
    boundStudies.delete(study);
    emitNqStatus();
    unsubscribe();
    ctx.signal?.removeEventListener('abort', onAbort);
    ctx.setDataRetry?.(null);
    ctx.removePrimitive?.(primitive);
  };
}

registerIndicator({
  id: ZERODAY_ID,
  name: 'Zeroday Scalper',
  category: 'FZN',
  placement: 'onchart',
  inputs: [
    { key: 'showStars', type: 'boolean', label: 'Stars', default: true },
    {
      key: 'marker',
      type: 'select',
      label: 'Marker',
      default: 'star',
      options: [
        { label: 'Star', value: 'star' },
        { label: 'Circle', value: 'circle' },
        { label: 'Diamond', value: 'diamond' },
        { label: 'Triangle', value: 'triangle' },
        { label: 'Square', value: 'square' },
      ],
    },
    { key: 'size', type: 'number', label: 'Size', default: 8, min: 4, max: 24, step: 1 },
    { key: 'offset', type: 'number', label: 'Offset', default: 16, min: 0, max: 64, step: 1 },
    { key: 'buyColor', type: 'color', label: 'Buy', default: BUY_COLOR },
    { key: 'sellColor', type: 'color', label: 'Sell', default: SELL_COLOR },
    { key: 'showDaily', type: 'boolean', label: 'Daily AVWAP', default: true },
    { key: 'showWeekly', type: 'boolean', label: 'Weekly AVWAP', default: true },
    { key: 'showTwoDay', type: 'boolean', label: '2-day AVWAP', default: true },
    { key: 'showEma15', type: 'boolean', label: '8 EMA 15-minute', default: true },
    { key: 'showEma65', type: 'boolean', label: '8 EMA 65-minute', default: true },
  ],
  plots: [
    {
      key: 'daily',
      type: 'line',
      title: 'Daily AVWAP',
      style: { color: '#4C8DFF', lineWidth: 1.5, priceLineVisible: false },
    },
    {
      key: 'weekly',
      type: 'line',
      title: 'Weekly AVWAP',
      style: { color: '#9B6BFF', lineWidth: 1.5, priceLineVisible: false },
    },
    {
      key: 'twoDay',
      type: 'line',
      title: '2-day AVWAP',
      style: { color: '#2EC4B6', lineWidth: 1.5, priceLineVisible: false },
    },
    {
      key: 'ema15',
      type: 'line',
      title: '8 EMA 15m',
      style: { color: '#E8872A', lineWidth: 1.5, priceLineVisible: false },
    },
    {
      key: 'ema65',
      type: 'line',
      title: '8 EMA 65m',
      style: { color: '#E8C547', lineWidth: 1.5, priceLineVisible: false },
    },
  ],
  calc(bars, settings, store): IndicatorValues {
    return projectLines(bars, settings, store);
  },
  attach(ctx): () => void {
    return attachZeroday(ctx);
  },
});
